/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import { activate } from "./activate";
import "./NonprofitNews.css";
import { SHOW_ERRORS, ErrorPreview } from "./ErrorPreview";

// Nonprofit news · the supporter's feed, for the Individual and Business
// workspaces.
//
// Laid out as the v2 desktop-1440 artboard has it: a column of post rows
// beside a 320px `WHO I FOLLOW` card. A row is a summary that goes somewhere
// — pressing it opens the nonprofit's public page on that post, which is
// where the post is drawn in full, with its gallery and its video. Nothing
// expands in place.
//
// Presentational and vm-driven throughout, like NonprofitProof: every state
// this renders (loading, failure, two empty states, a page of rows) is decided
// in newsVm and only drawn here.
class NonprofitNews extends Component {
  /**
   * One post. The whole card opens it, as in the design.
   *
   * Unread rows are tinted rather than merely dotted, which is what makes the
   * feed readable at a glance — and the tinting survives the badge being
   * cleared on arrival, so this visit still shows what was new.
   */
  renderRow(vm, row) {
    return (
      <div
        className={
          "Marketplace-News-card" +
          (row.unread ? " Marketplace-News-card--unread" : "") +
          (row.open ? " Marketplace-News-card--opens" : "")
        }
        {...activate(row.open)}
        key={row.id}
      >
        <div className="Marketplace-News-head">
          <span
            className={
              "Marketplace-News-dot" +
              (row.unread ? " Marketplace-News-dot--unread" : "")
            }
            title={row.unread ? vm.m.news.unreadDot : ""}
          />
          <span
            className="Marketplace-News-mono"
            style={{ background: row.monoBg, color: row.monoFg }}
          >
            {row.logo ? (
              <img className="Marketplace-News-monoImg" src={row.logo} alt="" />
            ) : (
              row.mono
            )}
          </span>
          <span className="Marketplace-News-org">{row.orgName}</span>
          <span className="Marketplace-News-chip">{row.chip}</span>
          <span className="Marketplace-News-when">{row.when}</span>
        </div>

        <div className="Marketplace-News-body">
          {/* Only when there is one. The design draws a dashed placeholder
              here; a real feed has text-only posts, and a permanent empty
              box on every one of them reads as a broken image. */}
          {!!row.thumbUrl && (
            <img
              className="Marketplace-News-thumb"
              src={row.thumbUrl}
              alt={row.thumbAlt}
            />
          )}
          <div className="Marketplace-News-text">
            <div className="Marketplace-News-title">{row.title}</div>
            <div className="Marketplace-News-excerpt">{row.excerpt}</div>
            <div className="Marketplace-News-foot">
              <span className="Marketplace-News-readFull">{row.readFull}</span>
              <span className="Marketplace-News-meta">{row.meta}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Following nobody is one click from being fixed and says so. Following
  // people who have not posted is not the reader's doing, so it gets no
  // button — there is nothing for them to press.
  renderEmpty(vm) {
    const n = vm.news;
    if (n.emptyNoFollows) {
      return (
        <div className="Marketplace-News-empty">
          <div className="Marketplace-News-emptyTitle">{n.emptyNoneTitle}</div>
          <div className="Marketplace-News-emptyBody">{n.emptyNoneBody}</div>
          <span
            className="Marketplace-News-emptyCta"
            {...activate(n.goDiscover)}
          >
            {n.emptyNoneCta}
          </span>
        </div>
      );
    }
    return (
      <div className="Marketplace-News-empty">
        <div className="Marketplace-News-emptyTitle">{n.emptyQuietTitle}</div>
        <div className="Marketplace-News-emptyBody">{n.emptyQuietBody}</div>
      </div>
    );
  }

  // The list, or whichever state stands in for it.
  renderFeed(vm, skipPreview) {
    const n = vm.news;
    if (SHOW_ERRORS && !skipPreview) {
      return (
        <React.Fragment>
          <div className="Marketplace-News-state">
            <ErrorPreview m={vm.m} keys={["news.error"]} style={{ display: "inline-block" }} />
            <span className="Marketplace-News-retry">{n.retryLabel}</span>
          </div>
          {this.renderFeed(vm, true)}
        </React.Fragment>
      );
    }
    if (n.loading) {
      return <div className="Marketplace-News-state">{n.loadingLabel}</div>;
    }
    if (n.error) {
      return (
        <div className="Marketplace-News-state">
          {n.errorLabel}
          <span className="Marketplace-News-retry" {...activate(n.retry)}>
            {n.retryLabel}
          </span>
        </div>
      );
    }
    if (n.empty) {
      return this.renderEmpty(vm);
    }
    return (
      <React.Fragment>
        {/* What was new when the reader arrived. The badge has already been
            cleared by getting here, so this is the only thing left saying
            so. */}
        {!!n.unreadNote && (
          <div className="Marketplace-News-unreadNote">{n.unreadNote}</div>
        )}
        <div className="Marketplace-News-list">
          {n.rows.map((row) => this.renderRow(vm, row))}
        </div>
        {n.hasMore && (
          <div className="Marketplace-News-more">
            <span
              className={
                "Marketplace-News-moreBtn" +
                (n.moreBusy ? " Marketplace-News-moreBtn--busy" : "")
              }
              {...activate(n.moreBusy ? null : n.loadMore)}
            >
              {n.moreLabel}
            </span>
          </div>
        )}
      </React.Fragment>
    );
  }

  /**
   * WHO I FOLLOW — who the feed is coming from, and the way to stop it.
   *
   * Absent until the feed has answered: an empty card is a claim that nobody
   * is followed, and that is not one to make before the list arrives. Absent
   * again once it says nobody is, because the empty state beside it already
   * says so and says it better.
   */
  renderFollows(vm) {
    const n = vm.news;
    if (!n.hasFollows || !n.follows.length) {
      return null;
    }
    return (
      <div className="Marketplace-News-rail">
        <div className="Marketplace-News-railCard">
          <div className="Marketplace-News-railTitle">{n.followTitle}</div>
          <div className="Marketplace-News-follows">
            {n.follows.map((f) => (
              <div className="Marketplace-News-follow" key={f.id}>
                <span
                  className="Marketplace-News-followMono"
                  style={{ background: f.monoBg, color: f.monoFg }}
                >
                  {f.logo ? (
                    <img
                      className="Marketplace-News-followMonoImg"
                      src={f.logo}
                      alt=""
                    />
                  ) : (
                    f.mono
                  )}
                </span>
                <div className="Marketplace-News-followMain">
                  <div
                    className={
                      "Marketplace-News-followName" +
                      (f.open ? " Marketplace-News-followName--link" : "")
                    }
                    {...activate(f.open)}
                  >
                    {f.name}
                  </div>
                  <div className="Marketplace-News-followNote">{f.note}</div>
                </div>
                {/* The same handler the nonprofit's own Follow button uses,
                    so the two cannot get out of step. */}
                <span
                  className={
                    "Marketplace-News-unfollow" +
                    (f.busy ? " Marketplace-News-unfollow--busy" : "")
                  }
                  {...activate(f.busy ? null : f.unfollow)}
                >
                  {f.unfollowLabel}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const vm = this.props.vm;
    return (
      <div className="Marketplace-News">
        <div className="Marketplace-News-main">{this.renderFeed(vm)}</div>
        {this.renderFollows(vm)}
      </div>
    );
  }
}

NonprofitNews.propTypes = {
  vm: PropTypes.object.isRequired,
};

export default NonprofitNews;
